import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink } from "../components/atoms";
import { FeeTierTable } from "../components/FeeTiers";
import { FEES, CREDIT_TERMS, PAID_BY_YOU } from "../data/content";

export default function Fees() {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <>
      <Seo
        title="Professional fees | 80 Metrics"
        description="Clear scope. Published fees. No surprises. SuburbIQ $220, Property & Portfolio Assessment $2,200, Buyer Advocacy from $13,200, Vendor Advocacy $4,400 — incl. GST."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Professional Fees", path: "/fees" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="fees-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Professional fees</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Clear scope. Published fees.</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="italic text-gold">No surprises.</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.8}>
            <p className="mt-8 text-sm lg:text-base leading-relaxed text-ivory/60 max-w-xl">
              You can engage any service directly. Earlier eligible payments may be credited once toward a later engagement.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white" data-testid="fees-scan">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="border-t border-charcoal/15">
            {FEES.map((f, i) => (
              <Reveal key={f.key} delay={i * 0.06}>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-7 lg:py-9 border-b border-charcoal/15">
                  <div className="sm:col-span-6">
                    <span className="text-xs font-semibold tracking-[0.26em] uppercase text-charcoal" data-testid={`fee-name-${f.key}`}>
                      {f.name}
                    </span>
                    <p className="mt-2 text-sm text-charcoal/60">{f.meta}</p>
                    <p className="mt-1 text-xs text-charcoal/45">{f.payment}</p>
                  </div>
                  <span className="sm:col-span-4 font-serif text-2xl lg:text-3xl font-medium text-golddark self-center" data-testid={`fee-price-${f.key}`}>
                    {f.key === "buyer" ? (
                      <a href="#buyer-fee-tiers" className="underline underline-offset-8 decoration-gold/50 hover:text-charcoal transition-colors duration-300">
                        {f.price}
                      </a>
                    ) : (
                      f.price
                    )}
                  </span>
                  <span className="sm:col-span-2 sm:text-right self-center">
                    <ArrowLink to={f.link} data-testid={`fee-link-${f.key}`}>Details</ArrowLink>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <a href="#details" data-testid="fees-details-anchor" className="group inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-charcoal hover:text-golddark transition-colors duration-300">
              <span>See full fee and credit details</span>
              <span className="inline-block transition-transform duration-300 group-hover:translate-y-1">↓</span>
            </a>
          </Reveal>
        </div>
      </section>

      <section id="buyer-fee-tiers" className="bg-white border-y border-charcoal/10" data-testid="buyer-fee-tiers-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal>
            <Eyebrow>Buyer Advocacy</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-charcoal">
              Total professional fee, by agreed purchase budget.
            </h2>
            <div className="mt-8 border border-charcoal/15 bg-ivory p-7 lg:p-9">
              <FeeTierTable />
            </div>
            <p className="mt-6 text-sm leading-relaxed text-charcoal/70" data-testid="fees-paid-by-you">
              {PAID_BY_YOU}
            </p>
          </Reveal>
        </div>
      </section>

      <section id="details" className="bg-bone" data-testid="fees-details">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28">
          <Reveal>
            <Eyebrow>The detail</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
              What each fee covers.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-charcoal/15 border border-charcoal/15">
            {FEES.map((f, i) => (
              <Reveal key={f.key} delay={i * 0.06} className="bg-ivory p-8 lg:p-12">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="font-serif text-2xl font-medium text-charcoal">{f.name}</h3>
                  <span className="text-sm font-semibold text-golddark">{f.price}</span>
                </div>
                <p className="mt-2 text-xs text-charcoal/50">{f.payment}</p>
                <ul className="mt-6 space-y-2.5 text-sm leading-relaxed text-charcoal/70">
                  {f.includes.map((inc) => (
                    <li key={inc} className="flex gap-3">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-gold rotate-45" />
                      {inc}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="fees-credits">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Fee credits</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
              Eligible payments can be credited once.
            </h2>
            <p className="mt-6 text-sm lg:text-base leading-relaxed text-charcoal/65">
              If you move from SuburbIQ or a Property & Portfolio Assessment into another eligible 80 Metrics service
              within the applicable timeframe, the eligible amount already paid may be credited once toward that later
              service. Credits reduce the later professional fee; they are not paid out in cash and cannot be used twice.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-gold/50 bg-bone p-8 lg:p-10" data-testid="credit-example">
              <p className="text-[11px] font-semibold tracking-[0.26em] uppercase text-golddark">Worked example</p>
              <div className="mt-5 space-y-3 text-sm text-charcoal/75">
                <p className="flex justify-between gap-6"><span>SuburbIQ report, paid up front</span><span className="font-semibold text-charcoal">$220</span></p>
                <p className="flex justify-between gap-6"><span>Property & Portfolio Assessment</span><span className="font-semibold text-charcoal">$2,200</span></p>
                <p className="flex justify-between gap-6"><span>SuburbIQ credited once (within 12 months)</span><span className="font-semibold text-golddark">− $220</span></p>
                <div className="border-t border-charcoal/15 pt-3 flex justify-between gap-6">
                  <span className="font-semibold text-charcoal">Assessment balance payable</span>
                  <span className="font-serif text-2xl font-medium text-charcoal">$1,980</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setTermsOpen((v) => !v)}
              data-testid="credit-terms-toggle"
              aria-expanded={termsOpen}
              className="mt-8 group inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-charcoal hover:text-golddark transition-colors duration-300"
            >
              <span>View full credit terms</span>
              <span className={`inline-block transition-transform duration-300 ${termsOpen ? "rotate-45" : ""}`}>+</span>
            </button>
            <AnimatePresence initial={false}>
              {termsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <ul className="mt-6 space-y-3 text-sm leading-relaxed text-charcoal/65 border-l-2 border-gold pl-6" data-testid="credit-terms-list">
                    {CREDIT_TERMS.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </section>
    </>
  );
}
